import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Card, 
  CardMedia, 
  CardContent, 
  Typography, 
  Button, 
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Alert,
  Box,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Close as CloseIcon, Error as ErrorIcon } from '@mui/icons-material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import VisorRA from './VisorRA';

const ModelGallery = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [openViewer, setOpenViewer] = useState(false);
  const [unsupportedModels, setUnsupportedModels] = useState([]);

  // Fetch models from Firestore
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setLoading(true);
        setError(null);
        setUnsupportedModels([]);
        
        const modelsCollection = collection(db, 'models');
        const modelsSnapshot = await getDocs(modelsCollection);
        
        const modelsData = modelsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Validate models
        const unsupported = [];
        const validModels = modelsData.filter(model => {
          if (!model.modelUrl || typeof model.modelUrl !== 'string' || model.modelUrl.trim() === '') {
            unsupported.push({id: model.id, reason: 'Invalid URL'});
            return false;
          }
          
          // Extract file extension
          const urlWithoutParams = model.modelUrl.split('?')[0];
          const extension = urlWithoutParams.split('.').pop().toLowerCase();
          
          return true;
        });

        setUnsupportedModels(unsupported);
        setModels(validModels);
        
        if (validModels.length === 0 && modelsData.length > 0) {
          setError("No valid model URLs found.");
        }
      } catch (err) {
        console.error("Error fetching models:", err);
        setError("Error loading models. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchModels();
  }, []);

  const handleOpenViewer = (model) => {
    setSelectedModel(model);
    setOpenViewer(true);
  };

  const handleCloseViewer = () => {
    setOpenViewer(false);
    setSelectedModel(null);
  };

  const getModelPreview = (model) => {
    if (!model.modelUrl) {
      return (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.05)'
        }}>
          <ErrorIcon color="disabled" sx={{ fontSize: 40 }} />
          <Typography variant="body2" color="text.disabled">
            Preview not available
          </Typography>
        </Box>
      );
    }

    const urlWithoutParams = model.modelUrl.split('?')[0];
    const extension = urlWithoutParams.split('.').pop().toLowerCase();
    const isSupportedFormat = ['glb', 'gltf', 'usdz'].includes(extension);

    return (
      <Box sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        position: 'relative'
      }}>
        {isSupportedFormat ? (
          <model-viewer
            style={{ 
              width: '100%', 
              height: '100%', 
              position: 'absolute',
              backgroundColor: '#eeeeee'
            }}
            src={model.modelUrl}
            camera-controls
            auto-rotate
            camera-orbit="0deg 75deg 105%"
            interaction-prompt="none"
            loading="eager"
          >
            <div slot="progress-bar" className="progress-bar"></div>
          </model-viewer>
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              .{extension} format not supported for preview
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Container sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '50vh',
        flexDirection: 'column'
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3 }}>
          Loading models...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert 
          severity="error" 
          icon={<ErrorIcon fontSize="large" />}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">{error}</Typography>
          {unsupportedModels.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                {unsupportedModels.length} models are not supported.
              </Typography>
            </Box>
          )}
        </Alert>
        <Button 
          variant="contained" 
          color="primary"
          size="large"
          onClick={() => window.location.reload()}
        >
          Try again
        </Button>
      </Container>
    );
  }

  if (models.length === 0) {
    return (
      <Container sx={{ 
        mt: 4, 
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '50vh',
        justifyContent: 'center'
      }}>
        <ErrorIcon color="disabled" sx={{ fontSize: 60, mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          No models available
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {unsupportedModels.length > 0 
            ? 'Existing models are not compatible.'
            : 'No models have been uploaded to the platform yet.'}
        </Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant={isMobile ? "h5" : "h4"} sx={{ mb: 4 }}>
        3D Model Gallery
      </Typography>
      
      <Grid container spacing={3}>
        {models.map((model) => {
          const urlWithoutParams = model.modelUrl?.split('?')[0];
          const extension = urlWithoutParams?.split('.').pop().toLowerCase();
          const isARSupported = ['glb', 'usdz'].includes(extension);
          
          return (
            <Grid item key={model.id} xs={12} sm={6} md={4}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)'
                }
              }}>
                <CardMedia
                  component="div"
                  sx={{
                    pt: '56.25%', // 16:9 aspect ratio
                    position: 'relative'
                  }}
                >
                  {getModelPreview(model)}
                </CardMedia>
                
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h6" component="div">
                    {model.name || 'Unnamed model'}
                  </Typography>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      mb: 1.5
                    }}
                  >
                    {model.description || 'No description'}
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Format:</strong> {extension?.toUpperCase() || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {model.createdAt ? 
                        new Date(model.createdAt).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Box>
                </CardContent>
                
                <Box sx={{ p: 2, pt: 0 }}>
                  <Button 
                    fullWidth
                    variant="contained"
                    onClick={() => handleOpenViewer(model)}
                    disabled={!model.modelUrl}
                    size={isMobile ? "small" : "medium"}
                  >
                    {isARSupported ? 'View in AR' : 'View model'}
                  </Button>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* AR Viewer Dialog */}
      <Dialog
        open={openViewer}
        onClose={handleCloseViewer}
        fullScreen={isMobile}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'black',
            overflow: 'hidden',
            height: '90vh'
          }
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleCloseViewer}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {selectedModel && (
            <VisorRA 
              modelUrl={selectedModel.modelUrl} 
              modelName={selectedModel.name}
              onClose={handleCloseViewer}
              isMobile={isMobile}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ModelGallery;